"use client";

import {
  Alert,
  AlertDescription,
} from "@nyntax/design-system/components/ui/alert";
import { Button } from "@nyntax/design-system/components/ui/button";
import { Field, FieldLabel } from "@nyntax/design-system/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@nyntax/design-system/components/ui/input-group";
import { useLockFn } from "@nyntax/hooks";
import { detectIdentifierType } from "@nyntax/contracts/schemas/auth";
import { useRouter } from "next/navigation";
import type { SubmitEvent } from "react";
import { useState } from "react";
import { LuLock, LuMail, LuPhone } from "react-icons/lu";
import { z } from "zod";

import { signIn } from "@/lib/auth";

import { GoogleButton } from "./google-button";
import type { GoogleButtonLabels } from "./google-button";

/** Translated strings the sign-in form shows. */
export interface SignInFormLabels {
  error: string;
  identifier: string;
  password: string;
  rateLimited: string;
  submit: string;
  submitting: string;
  unavailable: string;
}

interface SignInFormProps {
  /** Show the "Google did not work" message, after a failed round trip. */
  googleFailed?: boolean;
  googleLabels: GoogleButtonLabels;
  labels: SignInFormLabels;
  /** The locale segment, for the Google error return address. */
  locale: string;
  /** A same-site path, already checked on the server. */
  redirectTo: string;
}

type Phase = "idle" | "working" | "invalid" | "rate-limited" | "unavailable";

const credentialsSchema = z.object({
  identifier: z.string(),
  password: z.string(),
});

/**
 * Email or phone plus password against the shared ClassTablet account. The
 * API sets the session cookie on this host through the same-origin proxy, so
 * a full navigation afterwards makes the server pages see the session.
 *
 * @param props - Labels and the checked same-site redirect path.
 * @returns The sign-in form.
 */
export const SignInForm = ({
  googleFailed = false,
  googleLabels,
  labels,
  locale,
  redirectTo,
}: SignInFormProps) => {
  const router = useRouter();
  const [phase, setPhase] = useState<Phase>("idle");
  const [identifierType, setIdentifierType] = useState<"email" | "phone">(
    "email"
  );

  const submit = useLockFn(async (data: FormData) => {
    const credentials = credentialsSchema.safeParse(Object.fromEntries(data));

    if (!credentials.success) {
      setPhase("invalid");

      return;
    }

    const { identifier, password } = credentials.data;
    setPhase("working");

    try {
      const result = await signIn({ identifier, password });

      if (result === "ok") {
        router.push(redirectTo);
        router.refresh();

        return;
      }

      setPhase(result);
    } catch {
      setPhase("unavailable");
    }
  });

  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit(new FormData(event.currentTarget));
  };

  const messages: Partial<Record<Phase, string>> = {
    invalid: labels.error,
    "rate-limited": labels.rateLimited,
    unavailable: labels.unavailable,
  };

  const message = messages[phase];

  return (
    <div className="space-y-5">
      <GoogleButton
        callbackURL={redirectTo}
        errorCallbackURL={`/${locale}/sign-in?error=google&redirect=${encodeURIComponent(redirectTo)}`}
        failedOnLoad={googleFailed}
        labels={googleLabels}
      />
      <form className="space-y-5" noValidate onSubmit={onSubmit}>
        <Field>
          <FieldLabel>{labels.identifier}</FieldLabel>
          <InputGroup>
            <InputGroupAddon align="inline-start">
              {identifierType === "phone" ? <LuPhone /> : <LuMail />}
            </InputGroupAddon>
            <InputGroupInput
              autoComplete="username"
              name="identifier"
              onChange={(event) => {
                setIdentifierType(detectIdentifierType(event.target.value));
              }}
              required
              type="text"
            />
          </InputGroup>
        </Field>
        <Field>
          <FieldLabel>{labels.password}</FieldLabel>
          <InputGroup>
            <InputGroupAddon align="inline-start">
              <LuLock />
            </InputGroupAddon>
            <InputGroupInput
              autoComplete="current-password"
              name="password"
              required
              type="password"
            />
          </InputGroup>
        </Field>
        <Button
          className="w-full"
          disabled={phase === "working"}
          size="xl"
          type="submit"
        >
          {phase === "working" ? labels.submitting : labels.submit}
        </Button>
        {message !== undefined && (
          <Alert variant="error">
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}
      </form>
    </div>
  );
};
